import { HOLE_RADIUS, GOAL_RADIUS, PANEL_SIZE, FALL_ANIMATION_DURATION } from '../config.js';

/**
 * 负责：
 * - 检测球与陷阱洞/终点的 2D 距离
 * - 掉洞动画（物理暂停期间 tween Y 与透明度）
 * - 重置球位置
 */
export class BallController {
  /**
   * @param {object} deps
   * @param {import('./PhysicsWorld.js').PhysicsWorld} deps.physics
   * @param {import('./SceneBuilder.js').SceneBuilder} deps.scene
   * @param {(timeMs: number) => void} deps.onLevelComplete
   * @param {() => void} deps.onBallFell
   * @param {() => number} deps.getElapsedMs
   */
  constructor({ physics, scene, onLevelComplete, onBallFell, getElapsedMs }) {
    this.physics = physics;
    this.scene = scene;
    this.onLevelComplete = onLevelComplete;
    this.onBallFell = onBallFell;
    this.getElapsedMs = getElapsedMs;

    this.level = null;
    this.mode = 'idle'; // 'idle' | 'playing' | 'falling' | 'won'
    this.fallStartMs = 0;
  }

  startLevel(level) {
    this.level = level;
    this.mode = 'playing';
    this.scene.setBallVisible(true);
    this.scene.setBallOpacity(1);
    this.scene.setBallYOffset(0);
    this.physics.resetBall(level.start[0], level.start[1]);
  }

  restart() {
    if (!this.level) return;
    this.mode = 'playing';
    this.scene.setBallVisible(true);
    this.scene.setBallOpacity(1);
    this.scene.setBallYOffset(0);
    this.physics.resetBall(this.level.start[0], this.level.start[1]);
  }

  isPhysicsActive() {
    return this.mode === 'playing';
  }

  /** 每帧调用。dt in seconds, nowMs in ms */
  update(dt, nowMs) {
    if (!this.level) return;

    if (this.mode === 'falling') {
      this._tickFallAnim(nowMs);
      return;
    }

    if (this.mode !== 'playing') return;

    const body = this.physics.ballBody;
    if (!body) return;

    const half = PANEL_SIZE / 2;
    const gx = body.position.x + half;
    const gy = half - body.position.z;

    // 终点检测
    const dgx = gx - this.level.goal[0];
    const dgy = gy - this.level.goal[1];
    if (Math.hypot(dgx, dgy) < GOAL_RADIUS) {
      this.mode = 'won';
      this.onLevelComplete(this.getElapsedMs());
      return;
    }

    // 陷阱洞检测
    for (const [hx, hy] of this.level.holes) {
      if (Math.hypot(gx - hx, gy - hy) < HOLE_RADIUS) {
        this._beginFall(nowMs);
        this.onBallFell();
        return;
      }
    }
  }

  _beginFall(nowMs) {
    this.mode = 'falling';
    this.fallStartMs = nowMs;
    if (this.physics.ballBody) {
      this.physics.ballBody.velocity.set(0, 0, 0);
      this.physics.ballBody.angularVelocity.set(0, 0, 0);
    }
  }

  _tickFallAnim(nowMs) {
    const t = Math.min(1, (nowMs - this.fallStartMs) / FALL_ANIMATION_DURATION);
    this.scene.setBallYOffset(-t * 1.0);
    this.scene.setBallOpacity(1 - t);
    if (t >= 1) {
      this.mode = 'playing';
      this.scene.setBallOpacity(1);
      this.scene.setBallYOffset(0);
      this.physics.resetBall(this.level.start[0], this.level.start[1]);
    }
  }
}
